import clsx from 'clsx'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useSmartAccount from 'src/common/api-clients/safe-protocol-kit/useSmartAccount'
import { useProfile } from 'src/common/api-clients/thirdweb/useProfile'
import { useToastManager } from 'src/common/components/ToastManager/hooks/useToastManager'
import Button from 'src/common/ui-kit/Button/Button'
import Icon from 'src/common/ui-kit/Icon/Icon'
import Modal from 'src/common/ui-kit/Modal'
import { useActiveAccount, useActiveWallet } from 'thirdweb/react'
import { shortenAddress } from 'thirdweb/utils'
import { QRCodeSVG } from 'qrcode.react'
import useBreakpoint from 'src/common/hooks/ui/useBreakpoint'
import { AnyToken } from 'src/common/types/models/Tokens'
import TextField from 'src/common/ui-kit/TextField/TextField'
import TokenWithAmountSelector from 'src/common/components/TokenWithAmountSelector/TokenWithAmountSelector'
import Big from 'big.js'
import TransactionButton from 'src/common/components/Buttons/TransactionButton'
import useTransferToken from 'src/common/contracts/hooks/token/useTransferToken'
import { useAllTokensForOwner } from 'src/common/queries/useTokensForOwner/useAllTokensForOwner'
import useLockedBalance from '../../hooks/useLockedBalance'
import useBalance from 'src/common/contracts/hooks/token/useBalance'
import useConfig from 'src/config/hooks/useConfig'

interface ProfileCardProps {
  className?: string
}

const ProfileCard = ({ className }: ProfileCardProps) => {
  const wallet = useActiveWallet()
  const profile = useProfile()
  const account = useActiveAccount()
  const config = useConfig()
  const { smartAccountInfo } = useSmartAccount()
  const upToMd = useBreakpoint('md')

  const handleLogout = () => {
    wallet?.disconnect()
  }

  const { t } = useTranslation('wallet', { keyPrefix: 'profile' })
  const { addToast } = useToastManager()
  const copyAddress = () => {
    if (smartAccountInfo?.address) {
      navigator.clipboard.writeText(smartAccountInfo?.address || '')
      addToast(t('addressCopied'), {
        type: 'info',
      })
    }
  }

  const [receiveModalOpen, setReceiveModalOpen] = useState(false)
  const [sendModalOpen, setSendModal] = useState(false)
  const [showWithdrawalById, setShowWithdrawalById] = useState(false)

  const [sendAsset, setSendAsset] = useState<AnyToken | undefined>()
  const isSendNFT = sendAsset && sendAsset.type === 'ERC721'
  const [sendAmount, setSendAmount] = useState<Big>(Big(0))
  const [sendAddress, setSendAddress] = useState<string>('')
  const [nftIds, setNftIds] = useState<string>('')
  const sendDisabled =
    !sendAsset || !sendAddress || (sendAmount.eq(0) && !nftIds)

  const transferToken = useTransferToken()
  const transferTokenTx = useMemo(() => {
    if (sendDisabled) return undefined
    const nftIdsArr = nftIds
      .split(',')
      .filter(Boolean)
      .map(id => id.trim())
    return transferToken({
      token: sendAsset!,
      to: sendAddress,
      amount: sendAmount,
      tokenIds: nftIdsArr,
    })!
  }, [sendDisabled, sendAsset, sendAmount, transferToken, sendAddress, nftIds])
  const { refetch: lockedRefetch } = useLockedBalance()
  const { refetch: allTokensRefetch } = useAllTokensForOwner(
    smartAccountInfo?.address || ''
  )
  const { refetch: refetchSendBalance } = useBalance({
    token: sendAsset,
    owner: smartAccountInfo?.address,
  })
  const onTransferSuccess = () => {
    setSendModal(false)
    allTokensRefetch()
    lockedRefetch()
    refetchSendBalance?.()
  }

  if (!account) return null

  return (
    <div className={clsx('paper p-2 md:p-4 w-full mb-2', className)}>
      <div className='flex justify-between gap-4 items-start mb-4'>
        <div className='flex items-center gap-2'>
          <img
            className='w-12 h-auto rounded-full'
            src={profile.data.avatar}
            alt='Profile avatar'
          />
          <div>
            <h3
              className='flex items-center gap-2 mb-0 cursor-pointer typo-title2 font-bold lg:typo-heading1 lg:font-bold hover:text-main-muted duration-100 transition-colors'
              onClick={copyAddress}
              style={{
                marginBottom: '-4px',
              }}
            >
              {smartAccountInfo?.address
                ? shortenAddress(smartAccountInfo?.address, 6)
                : 'Connecting wallet...'}
              {smartAccountInfo?.address && <Icon name='copy' />}
            </h3>
            <p className='typo-label2'>{profile.data.email}</p>
          </div>
        </div>
        <div
          onClick={handleLogout}
          className='flex items-center gap-1 typo-button font-medium cursor-pointer hover:underline'
        >
          Logout
          <Icon name='logout' className='w-4 h-auto' />
        </div>
      </div>
      <div className='flex gap-2'>
        <Button
          variant='contained-light'
          className='flex-col items-start gap-2 px-4 py-4 min-w-36 w-6/12 sm:w-auto'
          onClick={() => setSendModal(true)}
          disabled={!smartAccountInfo?.address}
        >
          <Icon name='arrow-up-filled' />
          Send
        </Button>
        <Button
          variant='contained-light'
          className='flex-col items-start gap-2 px-4 py-4 min-w-36 w-6/12 sm:w-auto'
          onClick={() => setReceiveModalOpen(true)}
          disabled={!smartAccountInfo?.address}
        >
          <Icon name='arrow-down-filled' />
          Receive
        </Button>
      </div>
      <Modal open={sendModalOpen} onClose={() => setSendModal(false)}>
        <div className='modal-title'>Send</div>
        <div className='flex flex-col gap-2'>
          <TokenWithAmountSelector
            title='Asset and amount:'
            token={sendAsset}
            onTokenChange={setSendAsset}
            amount={sendAmount}
            onTokenAmountChange={setSendAmount}
            onlyOwned
            className='bg-main'
            amountControl
            content={
              isSendNFT ? (
                <p className='typo-label1'>
                  Withdraws NFTs on a First-in-First-out basis. For example,
                  inputting 100 will withdraw the 100 NFTs which have been in
                  the wallet the longest.
                  {!showWithdrawalById && (
                    <>
                      <br />
                      <br />
                      <span
                        className='text-link cursor-pointer'
                        onClick={() => setShowWithdrawalById(true)}
                      >
                        Want to withdraw specific NFTs?
                      </span>
                    </>
                  )}
                </p>
              ) : null
            }
          />
          {isSendNFT && showWithdrawalById && (
            <div className='flex flex-col  bg-main rounded-lg p-4'>
              <h6 className='typo-title1 mb-2'>
                Withdraw specific NFTs (optional):
              </h6>
              <TextField
                value={nftIds}
                onChange={setNftIds}
                className='mb-1'
                inputProps={{
                  placeholder: '1 or 1,922,101,4...',
                  className: 'border-none min-h-11',
                }}
              />
              <p className='typo-label1'>
                Single NFT id or a comma-separated list of NFT IDs that you want
                to transfer. Amount is ignored if this list is provided.
              </p>
            </div>
          )}
          <div className='flex flex-col  bg-main rounded-lg p-4'>
            <h6 className='typo-title1 mb-2'>Send to:</h6>
            <TextField
              value={sendAddress}
              onChange={setSendAddress}
              inputProps={{
                placeholder: '0x0000000000000000000000000000000000000000',
                className: 'border-none min-h-11',
              }}
            />
          </div>
          <TransactionButton
            transaction={transferTokenTx}
            disabled={sendDisabled}
            onSuccess={onTransferSuccess}
            requireTier2={false}
          >
            Send
          </TransactionButton>
        </div>
      </Modal>
      <Modal open={receiveModalOpen} onClose={() => setReceiveModalOpen(false)}>
        <div className='modal-title'>Receive</div>
        <div className='flex flex-col mb-2   bg-main rounded-lg p-4'>
          {smartAccountInfo?.address && (
            <QRCodeSVG
              size={165}
              value={smartAccountInfo?.address}
              className='rounded-md mb-2 mx-auto'
              bgColor='#f4f5f6'
            />
          )}
          <h6 className='typo-title1 mb-0'>Network:</h6>
          <div className='flex items-center gap-2 typo-button font-bold'>
            Polygon (POS)
          </div>
          <h6 className='typo-title1 mb-0'>Your address:</h6>
          <div
            className='flex items-center gap-2 typo-button font-bold cursor-pointer hover:text-main-muted duration-100 transition-colors'
            onClick={copyAddress}
          >
            {upToMd && smartAccountInfo?.address
              ? shortenAddress(smartAccountInfo?.address!, 10)
              : smartAccountInfo?.address}
            <Icon name='copy' />
          </div>
        </div>
        <p className='modal-description mb-0'>
          Copy your wallet address from this screen and send USDC from your
          exchange (e.g., Coinbase, Binance) or wallet (e.g., Metamask) to this
          address. <b>Make sure to select the Polygon (POS) network</b> to avoid
          losing funds. Once the transaction is processed, your USDC will appear
          in your wallet.
          {(window as any).MavaWebChatToggle && (
            <>
              <br />
              <br />
              If you have any questions or issues, feel free to{' '}
              <u
                className='cursor-pointer'
                onClick={() => (window as any).MavaWebChatToggle()}
              >
                contact us
              </u>
              .
            </>
          )}
        </p>
      </Modal>
    </div>
  )
}

export default ProfileCard
